import { useTranslation } from 'react-i18next';
import { isOpen } from '@nexus/core';
import { toggleDone, updateTask, withDueTime, type Task } from '@nexus/workspace';
import { useStore } from '../../db/context';
import { useUi } from '../../state/ui';
import { cn } from '../../lib/cn';
import { Calendar } from '../calendar/Calendar';
import { displayTitle, todayIso } from './format';
import { openRow } from '../../lib/peek';

/**
 * Tasks on their due date, by day, week or month. Dragging a task to another
 * day (or hour, in the day view) changes its due date.
 */
export function TaskCalendar({ tasks, place = 'tasks' }: { tasks: Task[]; place?: string }) {
  const { t } = useTranslation();
  const store = useStore();
  const open = useUi((s) => s.open);
  const today = todayIso();
  const items = tasks
    .filter((x) => x.due)
    .map((x) => ({ id: x.id, date: x.due!, time: x.dueTime, task: x }));
  const byId = new Map(items.map((i) => [i.id, i.task]));

  return (
    <div className="mt-4">
      <Calendar
        place={place}
        items={items}
        onDrop={(id, date, time) => {
          const task = byId.get(id);
          if (task)
            void updateTask(store, task, {
              due: time ? `${date} ${time}` : withDueTime(date, task),
            });
        }}
        renderItem={({ task: x, date }, compact, mode) => (
          <div
            className={cn(
              compact ? 'flex items-center gap-1.5' : 'flex items-start gap-1.5',
              compact ? 'text-xs' : 'rounded-lg bg-bg-subtle px-2 py-1 text-sm',
            )}
          >
            <input
              type="checkbox"
              checked={x.status === 'done'}
              aria-label={t('tasks.toggle', { title: x.title })}
              onChange={() => void toggleDone(store, x)}
              className={cn('nx-check shrink-0', compact && 'size-3.5!')}
            />
            <button
              type="button"
              onClick={(e) =>
                x.kind === 'page'
                  ? openRow(x.pageId, e)
                  : open({ kind: 'page', pageId: x.pageId }, e.ctrlKey || e.metaKey)
              }
              title={x.dueTime ? `${x.dueTime} ${x.title}` : x.title}
              className={cn(
                'min-w-0 text-left hover:underline',
                compact ? 'truncate' : 'break-words',
                !isOpen(x.status) && 'text-fg-muted line-through',
                isOpen(x.status) && date < today && 'text-danger',
              )}
            >
              {x.dueTime && mode !== 'day' && (
                <span className="mr-1 tabular-nums opacity-80">{x.dueTime}</span>
              )}
              {displayTitle(x.title)}
            </button>
          </div>
        )}
      />
    </div>
  );
}
